import { createAction } from '@reduxjs/toolkit';
import { Draft, produce } from 'immer';

import LoadingStatus from '@libs/loading-status';

import {
  CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_FAILURE,
  CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_REQUEST,
  CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_SUCCESS,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_FAILURE,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_REQUEST,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_SUCCESS,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_FAILURE,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_REQUEST,
  CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_SUCCESS,
} from '@app/store/actionTypes';
import { RootState } from '@app/store/types';

import EmailStyle from './types/EmailStyle';

export interface BrandSettingPayload {
  borderColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  logo: {
    base64: string;
    extension: string;
    height: number;
    width: number;
  };
  logoLink?: string;
  serviceDescription: string;
}

export function fetchEmailStyle() {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_REQUEST,
  };
}

export function fetchEmailStyleSuccess(payload: { emailBrand: EmailStyle }) {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_SUCCESS,
    payload,
  };
}

export function fetchEmailStyleFailure(payload: Error) {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_FAILURE,
    payload,
  };
}

export function resetEmailStyle() {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_REQUEST,
  };
}

export function resetEmailStyleSuccess() {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_SUCCESS,
  };
}

export function resetEmailStyleFailure() {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_STYLE_RESET_FAILURE,
  };
}

export function updateEmailSender(payload: { senderName: string }) {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_REQUEST,
    payload,
  };
}

export const updateEmailSenderSuccess = createAction(
  CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_SUCCESS,
  (payload: string) => ({ payload })
);

export function updateEmailSenderFailure() {
  return {
    type: CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_FAILURE,
  };
}

export type CustomBrandSettingEmailAction =
  | ReturnType<typeof fetchEmailStyle>
  | ReturnType<typeof fetchEmailStyleSuccess>
  | ReturnType<typeof fetchEmailStyleFailure>
  | ReturnType<typeof resetEmailStyle>
  | ReturnType<typeof resetEmailStyleSuccess>
  | ReturnType<typeof resetEmailStyleFailure>
  | ReturnType<typeof updateEmailSender>
  | ReturnType<typeof updateEmailSenderSuccess>
  | ReturnType<typeof updateEmailSenderFailure>;

type CustomBrandSettingEmailState = {
  borderColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  createdAt: string | null;
  emailStyleLoadingStatus: LoadingStatus;
  emailStyleUpdateLoadingStatus: LoadingStatus;
  isCustom: boolean;
  logo: {
    height: number;
    link: string;
    url: string;
    width: number;
  };
  senderName: string;
  senderNameChangeLoadingStatus: LoadingStatus;
  serviceDescription: string;
  updatedAt: string | null;
};

const initialState: CustomBrandSettingEmailState = {
  borderColor: '#2E3949',
  buttonBackgroundColor: '#FFC601',
  buttonTextColor: '#2E3949',
  createdAt: null,
  emailStyleLoadingStatus: LoadingStatus.IDLE,
  emailStyleUpdateLoadingStatus: LoadingStatus.IDLE,
  isCustom: false,
  logo: {
    height: 20,
    link: 'https://www.modusign.co.kr',
    url: 'https://s3.ap-northeast-2.amazonaws.com/modusign-email-custom-prod/service-logo/default/logo.jpeg',
    width: 139,
  },
  senderName: '모두싸인',
  senderNameChangeLoadingStatus: LoadingStatus.IDLE,
  serviceDescription: '간편 전자계약 모두싸인',
  updatedAt: null,
};

export const reducer = produce(
  (draft: Draft<CustomBrandSettingEmailState>, action: CustomBrandSettingEmailAction) => {
    switch (action.type) {
      case CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_REQUEST: {
        draft.emailStyleLoadingStatus = LoadingStatus.PENDING;
        return draft;
      }
      case CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_SUCCESS: {
        if (action.payload) {
          const {
            borderColor,
            buttonBackgroundColor,
            buttonTextColor,
            createdAt,
            isCustom,
            logo,
            senderName,
            serviceDescription,
            updatedAt,
          } = action.payload.emailBrand;
          draft.emailStyleLoadingStatus = LoadingStatus.SUCCEEDED;
          draft.borderColor = borderColor;
          draft.buttonBackgroundColor = buttonBackgroundColor;
          draft.buttonTextColor = buttonTextColor;
          draft.createdAt = createdAt;
          draft.isCustom = isCustom;
          draft.logo = logo;
          draft.senderName = senderName;
          draft.serviceDescription = serviceDescription;
          draft.updatedAt = updatedAt;
        }
        return draft;
      }
      case CUSTOM_BRAND_SETTING__EMAIL_STYLE_FETCH_FAILURE: {
        draft.emailStyleLoadingStatus = LoadingStatus.FAILED;
        return draft;
      }
      case CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_FAILURE: {
        draft.senderNameChangeLoadingStatus = LoadingStatus.FAILED;
        return draft;
      }
      case CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_REQUEST: {
        draft.senderNameChangeLoadingStatus = LoadingStatus.PENDING;
        return draft;
      }
      case CUSTOM_BRAND_SETTING__EMAIL_SENDER_UPDATE_SUCCESS: {
        if (action.payload) {
          draft.senderName = action.payload;
          draft.senderNameChangeLoadingStatus = LoadingStatus.SUCCEEDED;
        }
        return draft;
      }
      default:
        return draft;
    }
  },
  initialState
);

export const getEmail = (state: RootState) => state.customBrandSetting.email;

export const getSenderName = (state: RootState) => getEmail(state).senderName;

export const getSenderNameChangeLoadingStatus = (state: RootState) =>
  getEmail(state).senderNameChangeLoadingStatus;

export const getEmailStyleLoadingStatus = (state: RootState) =>
  getEmail(state).emailStyleLoadingStatus;

export const getEmailStyleUpdateLoadingStatus = (state: RootState) =>
  getEmail(state).emailStyleUpdateLoadingStatus;
